import { Input } from '@sixfold/common-ui';
import * as React from 'react';
import { useDebouncedCallback } from 'use-debounce';

/**
 * Customers copy-paste large lists of references (container/booking/order numbers, etc.) from Excel.
 * Copying an Excel column produces newline- or tab-separated values; pasted into a single-line input,
 * those separators would otherwise be silently dropped by the browser and merge distinct references
 * together. Normalize them to spaces so downstream space/comma-separated bulk search parsing works.
 *
 * Do not trim: Excel copies usually include a trailing newline (and no leading one). Those separators
 * become the boundary spaces that keep pasted tokens from gluing onto adjacent existing text after splice.
 */
function normalizeMultilinePaste(pastedText: string): string {
  return pastedText.replace(/[\r\n\t]+/g, ' ');
}

export interface InputProps {
  isLoading?: boolean;
  value?: string;
  placeholder: string;
  className?: string;
  autoFocus?: boolean;
  disabled?: boolean;
  size?: 'small' | 'medium';

  /* debounce the input onChange events (does not affect onClear) */
  debounceTimeout?: number;
  onChange?: (input: { value: string }) => void;
  onClear?: () => void;
  onFocus?: () => void;
  onBlur?: () => void;

  /**
   * SearchInput is reused across many unrelated search boxes (fleet, vehicles, notifications,
   * company users, etc). Multiline/tab paste normalization is only relevant for bulk-reference
   * search (e.g. trips/shipments search), so it's opt-in rather than applied everywhere.
   */
  normalizeMultilinePaste?: boolean;
}

export const SearchInput = React.forwardRef<HTMLInputElement, InputProps>(function SearchInput(
  {
    value,
    placeholder,
    autoFocus,
    className,
    onChange,
    onClear,
    onFocus,
    onBlur,
    debounceTimeout,
    isLoading,
    disabled,
    size,
    normalizeMultilinePaste: shouldNormalizeMultilinePaste,
  },
  ref,
) {
  const [stateValue, setStateValue] = React.useState<string>(value ?? '');
  const debouncedOnChange = useDebouncedCallback((args: { value: string }) => {
    onChange?.(args);
  }, debounceTimeout);
  const handleChange = React.useCallback(
    (args: { value: string }) => {
      setStateValue(args.value);
      debouncedOnChange(args);
    },
    [debouncedOnChange],
  );

  const handlePaste = React.useCallback(
    (event: React.ClipboardEvent<HTMLInputElement>) => {
      if (!shouldNormalizeMultilinePaste) {
        return;
      }

      const pastedText = event.clipboardData.getData('text');
      if (!/[\r\n\t]/.test(pastedText)) {
        return;
      }

      event.preventDefault();

      const target = event.currentTarget;
      const start = target.selectionStart ?? target.value.length;
      const end = target.selectionEnd ?? target.value.length;
      const newValue = target.value.slice(0, start) + normalizeMultilinePaste(pastedText) + target.value.slice(end);

      handleChange({ value: newValue });
    },
    [handleChange, shouldNormalizeMultilinePaste],
  );

  React.useEffect(() => {
    /**
     * SearchInput value has to be on state inside the component, as onChange is debounced.
     * This means that when defaultValue prop changes for any reason (ex URL is updated by some other logic), it wouldn't be reflected here.
     * As a workaround, copying prop to state when it changes.
     */
    setStateValue(value ?? '');
  }, [value]);

  return (
    <Input
      label={placeholder}
      labelPosition="hidden"
      placeholder={placeholder}
      name="search"
      icon="small-search"
      ref={ref}
      clearable
      onClear={onClear}
      loading={isLoading}
      value={stateValue}
      onChange={handleChange}
      onPaste={handlePaste}
      onFocus={onFocus}
      onBlur={onBlur}
      autoFocus={autoFocus}
      disabled={disabled}
      className={className}
      size={size}
    />
  );
});
